import classNames from "classnames";
import React, { FC, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { LuChevronRight, LuUsers, LuX } from "react-icons/lu";

import { useAppSelector } from "../../../../redux/redux";
import { getLinkedFirm } from "../../../../sessionStorage/sessionStorage";
import { useGetSearchCustomersQuery } from "../../../../slices/ContactsSlice";
import {
  useGetSeasonalQuery,
  useLazyGetSeatPreviewQuery,
} from "../../../../slices/SubscriptionSlice";
import {
  useAddUserMutation,
  useGetPermissionsQuery,
  useGetUserRoleEnumQuery,
} from "../../../../slices/UserSlice";
import { IUser } from "../../../../types/types";
import { AddSeatConfirmModal } from "../../../subscription/AddSeatConfirmModal";
import {
  seasonalInviteState,
  SeatPreview,
} from "../../../subscription/subscription.utils";
import { useSubscriptionStatus } from "../../../subscription/useSubscription";
import {
  AssignClientsModal,
  AssignedClient,
  toAssignedClient,
} from "../AssignClientsModal";
import {
  ADMIN_PERMISSIONS_NOTE,
  isAdminLevel,
  isIndependentPermission,
  matchAccessLevel,
  presetPermissions,
  sortPermissions,
  Toggle,
  visiblePermissions,
  withIndependentSwitches,
} from "../editUserParts";
import {
  ADMIN_PERMISSIONS_NOTE_SEATS,
  MANAGE_SWITCH_LOCKED_NOTE,
} from "../teamBilling";
import {
  ACCESS_LEVEL_CARDS,
  initialsOf,
  inviteFormProblems,
  permissionLabel,
  permissionShortHelp,
} from "./teamInvites.utils";

const LABEL = "mb-1.5 block text-[13.5px] font-bold text-[#293B5A]";
const SECTION =
  "mb-2.5 mt-1 text-[12px] font-bold uppercase tracking-[0.06em] text-[#8A90A2]";
const INPUT =
  "h-[42px] w-full rounded-[9px] border bg-white px-3.5 text-sm text-[#1F2433] outline-none focus:border-[#6B4EFF] focus:shadow-[0_0_0_3px_rgba(107,78,255,0.12)]";

const NAME_IN_USE =
  "There is already another user with this name, please choose a different name.";
const EMAIL_IN_USE =
  "The user with Email already exists. Try using another email.";

type Props = {
  onClose: () => void;
  /** Called after the invitation was sent, with the person's name. */
  onSent: (name: string) => void;
};

/**
 * "Invite a team member" (owner 2026-10-25): replaces the old Add user page. Same access levels,
 * switches and client assignment as Edit user; the invitation email is sent by the server.
 * Hourly rate and colour are set in Edit user once the person has joined (an invitation can't hold them).
 */
export const InviteMemberModal: FC<Props> = ({ onClose, onSent }) => {
  const me = useAppSelector((state) => state.appReducer.user) as IUser;
  const orgId = getLinkedFirm()?.orgId || "";
  const { data: allPermissions } = useGetPermissionsQuery();
  // Seat billing (2026-10-25): "Manage subscription & payment" only for firms billed per user,
  // and "Add a seat for {name}?" before the invitation goes out.
  const { status: billing } = useSubscriptionStatus(orgId);
  const [loadSeatPreview, { isFetching: isLoadingPreview }] =
    useLazyGetSeatPreviewQuery();
  const [seatPreview, setSeatPreview] = useState<SeatPreview | null>(null);
  // Free seasonal users (Feb 1 – Apr 30, one per paid user): no paid seat, no confirmation.
  const { data: seasonalInfo } = useGetSeasonalQuery(
    { orgId },
    { skip: !orgId || !billing?.seatBilling, refetchOnMountOrArgChange: true },
  );
  const seasonalState = seasonalInviteState(seasonalInfo);
  const [seasonal, setSeasonal] = useState(false);
  const isSeasonal = seasonal && seasonalState.show && !seasonalState.disabled;
  const permissions = useMemo(
    () =>
      visiblePermissions(allPermissions, {
        seatBilling: !!billing?.seatBilling,
      }),
    [allPermissions, billing?.seatBilling],
  );
  const { data: userRoleEnum } = useGetUserRoleEnumQuery();
  const { data: customers } = useGetSearchCustomersQuery({
    orgId,
    searchQuery: "",
    userId: me?.id,
    isActive: true,
  });
  const [addUser, { isLoading: isSending, error: rawError, reset }] =
    useAddUserMutation();

  const [form, setForm] = useState({ firstName: "", lastName: "", email: "" });
  const [touched, setTouched] = useState(false);
  const [level, setLevel] = useState("Staff (No Settings)");
  const [selected, setSelected] = useState<string[]>([]);
  const [assignedAll, setAssignedAll] = useState(true);
  const [assignedClients, setAssignedClients] = useState<AssignedClient[]>([]);
  const [isAssignOpen, setIsAssignOpen] = useState(false);

  const allPermissionIds = useMemo(
    () => (permissions ?? []).map((p) => p.id),
    [permissions],
  );
  const allClients = useMemo(
    () => (customers ?? []).map(toAssignedClient),
    [customers],
  );

  // Start as Staff with its switches once the permissions are known.
  useEffect(() => {
    if (allPermissionIds.length && selected.length === 0)
      setSelected(presetPermissions(level, allPermissionIds) ?? []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allPermissionIds]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isAssignOpen && !seatPreview) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, isAssignOpen, seatPreview]);

  const problems = inviteFormProblems(form);
  const error =
    typeof (rawError as unknown) === "string"
      ? (rawError as unknown as string)
      : "";
  const nameError =
    error === NAME_IN_USE ? "Someone on the team already has this name." : "";
  const emailError =
    error === EMAIL_IN_USE
      ? "This email already has an invitation or an account."
      : "";
  const generalError = error && !nameError && !emailError ? error : "";

  const pickLevel = (next: string) => {
    setLevel(next);
    const preset = presetPermissions(next, allPermissionIds);
    if (preset) setSelected(withIndependentSwitches(preset, selected));
  };

  const toggle = (id: string, checked: boolean) => {
    const next = checked
      ? [...selected.filter((i) => i !== id), id]
      : selected.filter((i) => i !== id);
    setSelected(next);
    setLevel(matchAccessLevel(next, allPermissionIds));
  };

  const set =
    (key: keyof typeof form) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (error) reset();
      setForm((current) => ({ ...current, [key]: event.target.value }));
    };

  const send = async () => {
    setTouched(true);
    if (Object.keys(problems).length) return;
    if (billing?.seatBilling && !seatPreview && !isSeasonal) {
      try {
        const preview = await loadSeatPreview({ orgId }).unwrap();
        if (preview.applies) {
          setSeatPreview(preview);
          return;
        }
      } catch {
        // Couldn't read the plan: send as before (the server adds the seat on acceptance anyway).
      }
    }
    await sendInvite();
  };

  const sendInvite = async () => {
    const roleId =
      userRoleEnum?.find((item) => item.userRole === level)?.id ||
      userRoleEnum?.find((item) => item.userRole === "Custom")?.id ||
      "";
    try {
      await addUser({
        orgId,
        userRoleEnumId: roleId,
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        // Admins: only the independent "Manage subscription & payment" switch is stored.
        permissionIds: isAdminLevel(level)
          ? selected.filter(isIndependentPermission)
          : selected,
        assignedAllClients: assignedAll,
        assignedCustomerIds: assignedAll
          ? []
          : assignedClients.map((client) => client.id),
        ...(isSeasonal ? { seasonal: true } : {}),
      }).unwrap();
      onSent(`${form.firstName.trim()} ${form.lastName.trim()}`);
    } catch {
      // shown below the fields
      setSeatPreview(null);
    }
  };

  const fullName = `${form.firstName} ${form.lastName}`.trim();
  const initials = initialsOf(form.firstName, form.lastName);
  const show = (key: keyof typeof problems) =>
    touched ? problems[key] : undefined;
  // A stand-in for the client picker (it only reads the name, email and who is searching).
  const pickerUser = {
    ...me,
    userProfile: {
      ...me?.userProfile,
      firstName: form.firstName,
      lastName: form.lastName,
      email: form.email,
    },
  } as IUser;

  return createPortal(
    <div
      className={
        "fixed inset-0 z-[1000] flex items-center justify-center bg-[rgba(17,20,34,0.45)] p-4 font-Manrope"
      }
      onMouseDown={(event) =>
        event.target === event.currentTarget && onClose()
      }>
      <div
        role={"dialog"}
        aria-modal={"true"}
        aria-label={"Invite a team member"}
        className={
          "flex max-h-[calc(100vh-32px)] w-[560px] max-w-full flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
        }>
        <div className={"relative border-b border-[#EDEFF3] px-6 py-5"}>
          <div className={"text-[18px] font-extrabold text-[#1F2433]"}>
            Invite a team member
          </div>
          <div className={"mt-1 text-[13px] text-[#7A7F8C]"}>
            They&apos;ll get an email to set up their password and sign in.
          </div>
          <button
            type={"button"}
            aria-label={"Close"}
            onClick={onClose}
            className={
              "absolute right-6 top-6 text-[#B4B9C6] hover:text-[#1F2433]"
            }>
            <LuX size={18} />
          </button>
        </div>

        <div className={"min-h-0 flex-1 overflow-y-auto px-6 py-5"}>
          <div
            className={
              "mb-5 flex items-center gap-3.5 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3.5"
            }>
            <span
              className={
                "flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#2E9E6A] text-[15px] font-bold text-white"
              }>
              {initials || <LuUsers size={18} />}
            </span>
            <div className={"min-w-0"}>
              <div
                className={
                  "truncate text-[15px] font-extrabold text-[#1F2433]"
                }>
                {fullName || "New team member"}
              </div>
              <div className={"truncate text-[12.5px] text-[#8A90A2]"}>
                {form.email.trim() || "Their email address"}
              </div>
            </div>
          </div>

          <div className={SECTION}>Details</div>
          <div className={"grid grid-cols-1 gap-3 sm:grid-cols-2"}>
            <div>
              <label htmlFor={"invite-first"} className={LABEL}>
                First name
              </label>
              <input
                id={"invite-first"}
                autoFocus={true}
                maxLength={100}
                value={form.firstName}
                onChange={set("firstName")}
                className={classNames(
                  INPUT,
                  show("firstName") || nameError
                    ? "border-[#E5484D]"
                    : "border-[#DCE2EA]",
                )}
              />
              <FieldError text={show("firstName") || nameError} />
            </div>
            <div>
              <label htmlFor={"invite-last"} className={LABEL}>
                Last name
              </label>
              <input
                id={"invite-last"}
                maxLength={100}
                value={form.lastName}
                onChange={set("lastName")}
                className={classNames(
                  INPUT,
                  show("lastName") ? "border-[#E5484D]" : "border-[#DCE2EA]",
                )}
              />
              <FieldError text={show("lastName")} />
            </div>
          </div>
          <div className={"mt-3"}>
            <label htmlFor={"invite-email"} className={LABEL}>
              Email
            </label>
            <input
              id={"invite-email"}
              type={"email"}
              maxLength={255}
              value={form.email}
              onChange={set("email")}
              className={classNames(
                INPUT,
                show("email") || emailError
                  ? "border-[#E5484D]"
                  : "border-[#DCE2EA]",
              )}
            />
            <FieldError text={show("email") || emailError} />
          </div>

          <div className={classNames(SECTION, "mt-5")}>Access level</div>
          <div
            role={"radiogroup"}
            aria-label={"Access level"}
            className={"mb-5 grid grid-cols-1 gap-2.5 sm:grid-cols-2"}>
            {ACCESS_LEVEL_CARDS.map((card) => {
              const on = level === card.level;
              return (
                <button
                  key={card.level}
                  type={"button"}
                  role={"radio"}
                  aria-checked={on}
                  onClick={() => pickLevel(card.level)}
                  className={classNames(
                    "rounded-xl border px-3.5 py-3 text-left transition-colors",
                    on
                      ? "border-[#6B4EFF] bg-[#F7F5FF] shadow-[inset_0_0_0_1px_#6B4EFF]"
                      : "border-[#DCE2EA] hover:border-[#C9C0FB]",
                  )}>
                  <span
                    className={classNames(
                      "block text-sm font-bold",
                      on ? "text-[#4B32D6]" : "text-[#1F2433]",
                    )}>
                    {card.title}
                  </span>
                  <span className={"block text-[12.5px] text-[#7A7F8C]"}>
                    {card.text}
                  </span>
                </button>
              );
            })}
          </div>

          {seasonalState.show && (
            <>
              <div className={SECTION}>Seasonal</div>
              <div
                className={
                  "mb-5 flex items-center gap-3 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4 py-3"
                }>
                <div className={"min-w-0 flex-1"}>
                  <div className={"text-[13.5px] font-semibold text-[#1F2433]"}>
                    Seasonal user (free)
                  </div>
                  <div
                    className={"text-[12px] text-[#7A7F8C]"}
                    data-testid={"seasonal-note"}>
                    {seasonalState.note}
                  </div>
                </div>
                <Toggle
                  checked={isSeasonal}
                  label={"Seasonal user (free)"}
                  disabled={seasonalState.disabled}
                  onChange={setSeasonal}
                />
              </div>
            </>
          )}

          <div className={SECTION}>Permissions</div>
          <div
            className={
              "mb-5 rounded-xl border border-[#EDEFF3] bg-[#F8F9FB] px-4"
            }>
            {isAdminLevel(level) && (
              <div
                className={
                  "border-b border-[#ECEFF3] py-3 text-[12.5px] font-medium text-[#6B7185]"
                }>
                {billing?.seatBilling
                  ? ADMIN_PERMISSIONS_NOTE_SEATS
                  : ADMIN_PERMISSIONS_NOTE}
              </div>
            )}
            {sortPermissions(permissions ?? []).map((permission) => {
              const independent = isIndependentPermission(permission.id);
              const admin = isAdminLevel(level) && !independent;
              const locked = independent && !billing?.canManage;
              const help = permissionShortHelp(permission.permission);
              return (
                <div
                  key={permission.id}
                  className={
                    "flex items-center gap-3 border-b border-[#ECEFF3] py-3 last:border-b-0"
                  }>
                  <div className={"min-w-0 flex-1"}>
                    <div
                      className={"text-[13.5px] font-semibold text-[#1F2433]"}>
                      {permissionLabel(permission.permission)}
                    </div>
                    {help && (
                      <div className={"text-[12px] text-[#7A7F8C]"}>{help}</div>
                    )}
                  </div>
                  <Toggle
                    checked={admin || selected.includes(permission.id)}
                    label={permissionLabel(permission.permission)}
                    disabled={admin || locked}
                    title={
                      admin
                        ? ADMIN_PERMISSIONS_NOTE
                        : locked
                        ? MANAGE_SWITCH_LOCKED_NOTE
                        : undefined
                    }
                    onChange={(checked) => toggle(permission.id, checked)}
                  />
                </div>
              );
            })}
            {!permissions && (
              <div className={"py-4 text-center text-[13px] text-[#8A90A2]"}>
                Loading permissions...
              </div>
            )}
          </div>

          <div className={SECTION}>Clients</div>
          <div
            className={
              "flex flex-wrap items-center gap-3.5 rounded-xl border border-[#EDEFF3] px-4 py-3.5"
            }>
            <span
              className={
                "flex h-10 w-10 items-center justify-center rounded-[10px] bg-[#F1F2F5] text-[#5B6478]"
              }>
              <LuUsers size={18} />
            </span>
            <div className={"min-w-0 flex-1"}>
              <div className={"text-sm font-bold text-[#1F2433]"}>
                Assigned clients
              </div>
              <div className={"text-[12.5px] text-[#7A7F8C]"}>
                {assignedAll
                  ? "All clients"
                  : assignedClients.length
                  ? `${assignedClients.length} client${
                      assignedClients.length === 1 ? "" : "s"
                    } chosen`
                  : "No clients chosen yet"}
              </div>
            </div>
            <div
              className={
                "inline-flex overflow-hidden rounded-[9px] border border-[#DCE2EA] text-[13px] font-semibold"
              }>
              <button
                type={"button"}
                onClick={() => setAssignedAll(true)}
                className={classNames(
                  "px-3 py-1.5",
                  assignedAll ? "bg-[#6B4EFF] text-white" : "text-[#3A4256]",
                )}>
                All clients
              </button>
              <button
                type={"button"}
                onClick={() => setIsAssignOpen(true)}
                className={classNames(
                  "flex items-center gap-1 px-3 py-1.5",
                  !assignedAll ? "bg-[#6B4EFF] text-white" : "text-[#3A4256]",
                )}>
                Choose… <LuChevronRight size={14} />
              </button>
            </div>
          </div>

          {generalError && (
            <div
              role={"alert"}
              className={
                "mt-4 rounded-lg border border-[#F5D0D0] bg-[#FDF3F3] px-3 py-2 text-[13px] text-[#B42318]"
              }>
              {generalError}
            </div>
          )}
        </div>

        <div
          className={
            "flex flex-none flex-wrap items-center justify-between gap-2.5 border-t border-[#EDEFF3] px-6 py-4"
          }>
          <span className={"text-[12.5px] text-[#8A90A2]"}>
            You can change all of this later.
          </span>
          <div className={"flex gap-2.5"}>
            <button
              type={"button"}
              onClick={onClose}
              disabled={isSending}
              className={
                "h-[38px] rounded-lg border border-[#DCE2EA] bg-white px-[22px] text-sm font-bold text-[#3A4256] hover:bg-[#F8F9FB]"
              }>
              Cancel
            </button>
            <button
              type={"button"}
              onClick={() => void send()}
              disabled={isSending || isLoadingPreview}
              className={
                "h-[38px] rounded-lg bg-[#6B4EFF] px-6 text-sm font-bold text-white shadow-[0_4px_14px_rgba(107,78,255,0.28)] hover:bg-[#5A3EEB] disabled:cursor-not-allowed disabled:bg-[#C3C8D4] disabled:shadow-none"
              }>
              {isSending
                ? "Sending…"
                : isLoadingPreview
                ? "Checking plan…"
                : "Send invitation"}
            </button>
          </div>
        </div>
      </div>
      {seatPreview && (
        <AddSeatConfirmModal
          preview={seatPreview}
          name={fullName}
          busy={isSending}
          onCancel={() => setSeatPreview(null)}
          onConfirm={() => void sendInvite()}
        />
      )}
      {isAssignOpen && (
        <AssignClientsModal
          user={pickerUser}
          avatarColor={"#2E9E6A"}
          allClients={allClients}
          assignedAll={assignedAll}
          assignedClients={assignedClients}
          onClose={() => setIsAssignOpen(false)}
          onSave={(all, clients) => {
            setAssignedAll(all);
            setAssignedClients(clients);
            setIsAssignOpen(false);
          }}
        />
      )}
    </div>,
    document.body,
  );
};

const FieldError: FC<{ text?: string }> = ({ text }) =>
  text ? (
    <div className={"mt-1 text-[12.5px] font-medium text-[#D92D20]"}>
      {text}
    </div>
  ) : null;
